// drive.ts — kontrakt analityczny dostaw, widziany od strony Control Center.
//
// Typy spisane z realnych odpowiedzi backendu Drive (tuszama_kod/backend/routes/analytics.js).
// Wszystkie liczby pochodzą z klasyfikacji UTRWALONEJ w chwili dostawy — nigdy z przeliczenia
// względem bieżącej godziny przerwy.

import type { ApiClient } from './client';
import type { BackendDataQuality } from './analytics';

export interface DeliveryMetrics {
  deliveries: number;
  on_time: number;
  /** Dostawy przed planowaną godziną. NIE są punktualnością i NIE są spóźnieniem. */
  early: number;
  late: number;
  late_5: number;
  late_15: number;
  late_over_15: number;
  /** null gdy nie było ani jednej dostawy — nigdy 0%. */
  on_time_pct: number | null;
  late_pct: number | null;
  /** Liczone TYLKO po dostawach, które mają zapisane odchylenie. */
  avg_delta_min: number | null;
  median_delta_min: number | null;
  without_snapshot: number;
  without_delta: number;
  data_quality: BackendDataQuality;
  data_quality_reason: string;
}

export interface DeliverySummaryResponse {
  range: { date_from: string; date_to: string; comparison: { date_from: string; date_to: string }; days: number };
  current: DeliveryMetrics;
  previous: DeliveryMetrics;
  on_time_delta_points: number | null;
  avg_delta_change_min: number | null;
  caveats: string[];
}

export interface DeliveryEntityRow {
  city: string | null;
  /** Identyfikator systemu głównego — klucz złączenia ze sprzedażą. null = rekord nie wejdzie do analizy łączonej. */
  tuszama_id: number | null;
  without_tuszama_id: number;
  current: DeliveryMetrics;
  previous: DeliveryMetrics;
  on_time_delta_points: number | null;
  avg_delta_change_min: number | null;
  data_quality: BackendDataQuality;
  data_quality_reason: string;
}

export interface DeliverySchoolRow extends DeliveryEntityRow {
  school_id: number;
  school_name: string | null;
}

export interface DeliveryRestaurantRow extends DeliveryEntityRow {
  restaurant_id: number;
  restaurant_name: string | null;
}

export interface DeliveryBySchoolResponse {
  range: DeliverySummaryResponse['range'];
  count: number;
  schools: DeliverySchoolRow[];
}

export interface DeliveryByRestaurantResponse {
  range: DeliverySummaryResponse['range'];
  count: number;
  restaurants: DeliveryRestaurantRow[];
}

export interface DeliveryTrendPoint extends DeliveryMetrics {
  date: string;
}

export interface DeliveryTrendResponse {
  range: { date_from: string; date_to: string };
  granularity: 'day' | 'week';
  count: number;
  series: DeliveryTrendPoint[];
  direction: 'up' | 'down' | 'flat' | 'unknown';
}

export interface DeliveryHistoryRow {
  id: number;
  delivery_date: string;
  school_name: string | null;
  restaurant_name: string | null;
  /** Pseudonimowy identyfikator kierowcy (deliveries.driver_id). Bez imienia i nazwiska. */
  driver_ref: number | null;
  planned_time: string | null;
  actual_time: string | null;
  delta_min: number | null;
  status: string;
  source: string | null;
  in_window: boolean | null;
  school_tuszama_id: number | null;
  restaurant_tuszama_id: number | null;
  data_quality: BackendDataQuality;
}

export interface DeliveryHistoryResponse {
  range: { date_from: string; date_to: string };
  /** Liczba ZWRÓCONYCH rekordów. */
  count: number;
  /**
   * Liczba rekordów PASUJĄCYCH do zapytania, przed nałożeniem limitu.
   * Opcjonalne, bo starszy backend tego pola nie ma — wtedy nie da się stwierdzić ucięcia
   * i interfejs nie ma prawa twierdzić, że lista jest kompletna.
   */
  total?: number;
  limit?: number;
  /** Czy lista została ucięta. Serwer mówi to wprost, żeby UI nie musiał zgadywać z liczb. */
  truncated?: boolean;
  history: DeliveryHistoryRow[];
}

export interface CoverageEntity {
  total: number;
  with_tuszama_id: number;
  without_tuszama_id: number;
  coverage_pct: number | null;
  distinct_ids: number;
}

export interface CoverageResponse {
  range: { date_from: string; date_to: string };
  deliveries: {
    total: number;
    with_school_tuszama_id: number;
    with_restaurant_tuszama_id: number;
    joinable: number;
    joinable_pct: number | null;
    distinct_school_ids: number;
    distinct_restaurant_ids: number;
  };
  schools: CoverageEntity;
  restaurants: CoverageEntity;
  note: string;
}

export interface DeliveryQuery {
  date_from?: string;
  date_to?: string;
  school_id?: number;
  restaurant_id?: number;
  granularity?: 'day' | 'week';
  limit?: number;
}

const BASE = '/api/admin/analytics';

export function createDriveApi(client: ApiClient) {
  return {
    summary: (q: DeliveryQuery = {}) => client.get<DeliverySummaryResponse>('drive', `${BASE}/delivery-summary`, { ...q }),
    bySchool: (q: DeliveryQuery = {}) => client.get<DeliveryBySchoolResponse>('drive', `${BASE}/delivery-by-school`, { ...q }),
    byRestaurant: (q: DeliveryQuery = {}) =>
      client.get<DeliveryByRestaurantResponse>('drive', `${BASE}/delivery-by-restaurant`, { ...q }),
    trend: (q: DeliveryQuery = {}) => client.get<DeliveryTrendResponse>('drive', `${BASE}/delivery-trend`, { ...q }),
    history: (q: DeliveryQuery = {}) => client.get<DeliveryHistoryResponse>('drive', `${BASE}/delivery-history`, { ...q }),
    coverage: (q: DeliveryQuery = {}) => client.get<CoverageResponse>('drive', `${BASE}/tuszama-id-coverage`, { ...q }),
  };
}

export type DriveApi = ReturnType<typeof createDriveApi>;
